import React, { useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useListOrderDetailByIdForAdminQuery, useUpdateOrderForAdminMutation } from '../../../api/order.api'
import { useListAllQuery as useListAllProductsQuery } from '../../../api/product.api'
import AdminTitle from '../../../common/AdminTitle/AdminTitle'
import OrdersForm from './OrdersForm'
import './AdminOrderPremium.css'
import { toast } from 'react-toastify'

const AdminOrdersView = ({ defaultEdit = false }) => {
    const params = useParams()
    const [searchParams, setSearchParams] = useSearchParams()
    const [order, setOrder] = useState()
    const [loading, setLoading] = useState(false)
    const [isEditing, setIsEditing] = useState(searchParams.get('edit') === 'true' || defaultEdit)
    const [editOrder] = useUpdateOrderForAdminMutation()
    const { data: orders, isLoading } = useListOrderDetailByIdForAdminQuery(params.id)
    const { data: productData } = useListAllProductsQuery({ page: 1, limit: 100 })
    const productList = productData?.result || []
    const navigate = useNavigate()

    useEffect(() => {
        if (searchParams.get('edit') === 'true' || defaultEdit) {
            setIsEditing(true)
        }
    }, [searchParams, defaultEdit])

    const handleToggleEdit = (active) => {
        setIsEditing(active)
        if (active) {
            setSearchParams({ edit: 'true' })
        } else {
            setSearchParams({})
        }
    }

    const [formData, setFormData] = useState({
        customerName: '',
        email: '',
        phone: '',
        optionalNumber: '',
        country: 'Nepal',
        address: '',
        landMark: '',
        city: '',
        height: '',
        weight: '',
        orderStatus: 'pending',
        paymentStatus: 'pending',
        paymentType: 'cod',
        serviceCharge: 0,
        discount: 0,
        total: 0,
        advancePayment: 0,
        remainingAmount: 0,
        items: []
    })

    const resetFormFromOrder = (orderData) => {
        if (!orderData) return
        const tot = Number(orderData.total || 0)
        const adv = Number(orderData.advancePayment || 0)
        const rem = orderData.remainingAmount !== undefined ? Number(orderData.remainingAmount) : Math.max(0, tot - adv)
        setFormData({
            customerName: orderData.customer?.fullname || orderData.customerName || '',
            email: orderData.customer?.email || orderData.email || '',
            phone: orderData.customer?.phone || orderData.number || '',
            optionalNumber: orderData.customer?.optionalNumber || orderData.optionalNumber || '',
            country: orderData.customer?.country || orderData.country || 'Nepal',
            address: orderData.customer?.address || orderData.address || '',
            landMark: orderData.customer?.landMark || orderData.landMark || '',
            city: orderData.customer?.city || orderData.city || '',
            height: orderData.height || orderData.customer?.height || '',
            weight: orderData.weight || orderData.customer?.weight || '',
            orderStatus: orderData.orderStatus || 'pending',
            paymentStatus: orderData.paymentStatus || 'pending',
            paymentType: orderData.paymentType || 'cod',
            serviceCharge: orderData.serviceCharge || 0,
            discount: orderData.discount || 0,
            total: tot,
            advancePayment: adv,
            remainingAmount: rem,
            items: orderData.items ? orderData.items.map(it => ({ ...it })) : []
        })
    }

    useEffect(() => {
        if (orders?.detail) {
            setOrder(orders.detail)
            resetFormFromOrder(orders.detail)
        }
    }, [orders])

    // Quick status submit from Operations card
    const submitQuickStatus = async (data) => {
        setLoading(true)
        try {
            const statusValue = data.orderStatus?.value || data.orderStatus
            const res = await editOrder({
                orderId: params.id, 
                payload: { orderStatus: statusValue }
            }).unwrap()
            toast.success("Order status updated successfully")
            if (res?.result) {
                setOrder(res.result)
                resetFormFromOrder(res.result)
            } else {
                setOrder(prev => ({ ...prev, orderStatus: statusValue }))
            }
        } catch (err) {
            toast.error(err?.data?.message || "Operation failed")
        } finally {
            setLoading(false)
        }
    }

    // Handle input field changes in edit mode
    const handleFieldChange = (field, value) => {
        setFormData(prev => {
            const updated = { ...prev, [field]: value }
            // Auto recalculate total if discount or shipping changes
            if (field === 'discount' || field === 'serviceCharge') {
                const subTotal = (updated.items || []).reduce((sum, it) => sum + (Number(it.price || 0) * Number(it.quantity || 1)), 0)
                const disc = Number(field === 'discount' ? value : updated.discount) || 0
                const ship = Number(field === 'serviceCharge' ? value : updated.serviceCharge) || 0
                updated.total = Math.max(0, subTotal - disc + ship)
            }
            if (field === 'total' || field === 'discount' || field === 'serviceCharge' || field === 'advancePayment') {
                const adv = Number(field === 'advancePayment' ? value : updated.advancePayment) || 0
                const curTotal = Number(updated.total) || 0
                updated.remainingAmount = Math.max(0, curTotal - adv)
            }
            return updated
        })
    }

    // Handle item changes (quantity or size only — price is immutable from product)
    const handleItemChange = (index, field, value) => {
        setFormData(prev => {
            const newItems = [...prev.items]
            newItems[index] = { ...newItems[index], [field]: value }
            if (field === 'quantity') {
                const q = Number(value) || 1
                const p = Number(newItems[index].price) || 0
                newItems[index].amount = q * p
            }
            const subTotal = newItems.reduce((sum, it) => sum + (Number(it.price || 0) * Number(it.quantity || 1)), 0)
            const disc = Number(prev.discount) || 0
            const ship = Number(prev.serviceCharge) || 0
            const newTotal = Math.max(0, subTotal - disc + ship)
            const adv = Number(prev.advancePayment) || 0
            return {
                ...prev,
                items: newItems,
                total: newTotal,
                remainingAmount: Math.max(0, newTotal - adv)
            }
        })
    }

    // Add a blank product row
    const handleAddItem = () => {
        setFormData(prev => ({
            ...prev,
            items: [...prev.items, { productId: '', title: '', size: '', quantity: 1, price: 0, cost: 0, amount: 0, productImage: null, _isNew: true }]
        }))
    }

    // Remove a product row (must keep at least 1)
    const handleRemoveItem = (index) => {
        setFormData(prev => {
            if (prev.items.length <= 1) return prev
            const newItems = prev.items.filter((_, i) => i !== index)
            const subTotal = newItems.reduce((sum, it) => sum + (Number(it.price || 0) * Number(it.quantity || 1)), 0)
            const disc = Number(prev.discount) || 0
            const ship = Number(prev.serviceCharge) || 0
            const newTotal = Math.max(0, subTotal - disc + ship)
            const adv = Number(prev.advancePayment) || 0
            return {
                ...prev,
                items: newItems,
                total: newTotal,
                remainingAmount: Math.max(0, newTotal - adv)
            }
        })
    }

    // When a product is selected from dropdown — auto-fill price, title, image
    const handleProductSelect = (index, productId) => {
        const product = productList.find(p => p._id === productId)
        if (!product) return
        setFormData(prev => {
            const newItems = [...prev.items]
            newItems[index] = {
                ...newItems[index],
                productId: product._id,
                title: product.title,
                price: product.price || 0,
                cost: product.cost || 0,
                productImage: product.images?.[0] || product.image || null,
                size: '', // reset size when product changes
                quantity: 1,
                amount: product.price || 0,
                _isNew: true
            }
            const subTotal = newItems.reduce((sum, it) => sum + (Number(it.price || 0) * Number(it.quantity || 1)), 0)
            const disc = Number(prev.discount) || 0
            const ship = Number(prev.serviceCharge) || 0
            const newTotal = Math.max(0, subTotal - disc + ship)
            const adv = Number(prev.advancePayment) || 0
            return {
                ...prev,
                items: newItems,
                total: newTotal,
                remainingAmount: Math.max(0, newTotal - adv)
            }
        })
    }

    // Save full order details
    const handleSaveFullOrder = async (e) => {
        if (e) e.preventDefault()
        setLoading(true)
        try {
            const payload = {
                customerName: formData.customerName,
                email: formData.email || null,
                phone: formData.phone,
                optionalNumber: formData.optionalNumber || null,
                country: formData.country || 'Nepal',
                address: formData.address,
                landMark: formData.landMark || null,
                city: formData.city || null,
                height: formData.height || null,
                weight: formData.weight || null,
                orderStatus: formData.orderStatus,
                paymentStatus: formData.paymentStatus,
                paymentType: formData.paymentType,
                serviceCharge: Number(formData.serviceCharge || 0),
                discount: Number(formData.discount || 0),
                total: Number(formData.total || 0),
                advancePayment: Number(formData.advancePayment || 0),
                remainingAmount: Math.max(0, Number(formData.total || 0) - Number(formData.advancePayment || 0)),
                items: formData.items.map(it => ({
                    productId: it.productId,
                    title: it.title,
                    size: it.size,
                    quantity: Number(it.quantity),
                    price: Number(it.price),
                    cost: Number(it.cost || 0),
                    amount: Number(it.amount || (it.price * it.quantity)),
                    productImage: it.productImage || null,
                    height: it.height || formData.height || null,
                    weight: it.weight || formData.weight || null
                }))
            }

            const res = await editOrder({ orderId: params.id, payload }).unwrap()
            toast.success("Full order details updated successfully!")
            if (res?.result) {
                setOrder(res.result)
                resetFormFromOrder(res.result)
            }
            handleToggleEdit(false)
        } catch (err) {
            console.error("Full order update error:", err)
            toast.error(err?.data?.message || err?.message || "Failed to update order details")
        } finally {
            setLoading(false)
        }
    }

    if (isLoading || !order) return <div style={{ padding: '60px', textAlign: 'center', color: '#6366f1' }}>Synchronizing Order Data...</div>

    return (
        <div className="admin_margin_box">
            <div className='ma-order-page-wrapper'>
                <div className="ma-order-header">
                    <div className="ma-order-title-group">
                        <AdminTitle label1="Order List" label2="/Order Details" url="/dashboard/order_list" />
                        <h1>Reference #{order.orderId}</h1>
                    </div>
                    <div className="ma-order-edit-header-actions">
                        <div className={`ma-order-badge ${order.orderStatus?.toLowerCase()}`} style={{ fontSize: '14px', padding: '8px 24px' }}>
                            {order.orderStatus}
                        </div>
                        {!isEditing ? (
                            <button 
                                className="ma-order-btn-primary" 
                                onClick={() => handleToggleEdit(true)}
                                style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                            >
                                <span>✏️</span> Edit Order Details
                            </button>
                        ) : (
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <button 
                                    className="ma-order-btn-secondary" 
                                    onClick={() => {
                                        resetFormFromOrder(order)
                                        handleToggleEdit(false)
                                    }}
                                    disabled={loading}
                                >
                                    Cancel
                                </button>
                                <button 
                                    className="ma-order-btn-save" 
                                    onClick={handleSaveFullOrder}
                                    disabled={loading}
                                >
                                    {loading ? 'Saving...' : '💾 Save Changes'}
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                <div className="ma-order-detail-grid">
                    <div className="ma-order-main-col">
                        {/* Items Card */}
                        <div className="ma-order-content-card">
                            <div className="ma-order-card-header" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ color: 'var(--ma-primary)' }}>📦</span> Inventory Breakdown
                                {isEditing && (
                                    <button
                                        type="button"
                                        onClick={handleAddItem}
                                        style={{
                                            marginLeft: 'auto',
                                            background: 'var(--ma-primary)',
                                            color: '#fff',
                                            border: 'none',
                                            borderRadius: '6px',
                                            padding: '4px 12px',
                                            fontSize: '12px',
                                            cursor: 'pointer',
                                            fontWeight: 600
                                        }}
                                    >
                                        + Add Product
                                    </button>
                                )}
                            </div>
                            <div className="ma-order-item-listing">
                                {(isEditing ? formData.items : order.items).map((item, idx) => (
                                    <div key={idx} className="ma-order-single-item" style={{ flexDirection: 'column', alignItems: 'stretch', gap: '10px' }}>
                                        {!isEditing ? (
                                            /* ── VIEW MODE ── */
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                <img src={item.productImage} className="ma-order-item-img" alt="" />
                                                <div className="ma-order-item-info" style={{ flex: 1 }}>
                                                    <span className="ma-order-item-name">{item.title}</span>
                                                    <span className="ma-order-item-sub">
                                                        Size: <strong>{item.size}</strong> | Qty: <strong>{item.quantity}</strong>
                                                        {(item.height || item.weight) && ` | H: ${item.height || '—'} W: ${item.weight || '—'}`}
                                                    </span>
                                                </div>
                                                <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                    <div style={{ fontWeight: 700, color: '#111827' }}>Rs. {Number(item.price * item.quantity).toLocaleString()}</div>
                                                    <div style={{ fontSize: '10px', color: 'var(--ma-text-muted)' }}>Unit: Rs. {item.price}</div>
                                                </div>
                                            </div>
                                        ) : (
                                            /* ── EDIT MODE ── */
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '10px', background: 'rgba(0,0,0,0.02)', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                                                {/* Row 1: product image + name / product selector for new items */}
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                    {item.productImage && <img src={item.productImage} className="ma-order-item-img" alt="" style={{ width: '44px', height: '44px', borderRadius: '6px', objectFit: 'cover', flexShrink: 0 }} />}
                                                    <div style={{ flex: 1 }}>
                                                        {item._isNew ? (
                                                            <>
                                                                <label style={{ fontSize: '10px', color: '#64748b', fontWeight: 'bold', display: 'block', marginBottom: '3px' }}>SELECT PRODUCT</label>
                                                                <select
                                                                    className="ma-order-edit-input"
                                                                    style={{ width: '100%' }}
                                                                    value={item.productId || ''}
                                                                    onChange={(e) => handleProductSelect(idx, e.target.value)}
                                                                >
                                                                    <option value="">-- Choose a product --</option>
                                                                    {productList.map(p => (
                                                                        <option key={p._id} value={p._id}>{p.title} — Rs. {p.price}</option>
                                                                    ))}
                                                                </select>
                                                            </>
                                                        ) : (
                                                            <span className="ma-order-item-name" style={{ fontSize: '13px' }}>{item.title}</span>
                                                        )}
                                                    </div>
                                                    {/* Remove button — always show in edit, keep at least 1 */}
                                                    {formData.items.length > 1 && (
                                                        <button
                                                            type="button"
                                                            onClick={() => handleRemoveItem(idx)}
                                                            title="Remove this product"
                                                            style={{
                                                                background: '#fee2e2',
                                                                color: '#dc2626',
                                                                border: 'none',
                                                                borderRadius: '6px',
                                                                padding: '4px 10px',
                                                                fontSize: '12px',
                                                                cursor: 'pointer',
                                                                fontWeight: 600,
                                                                flexShrink: 0
                                                            }}
                                                        >
                                                            ✕ Remove
                                                        </button>
                                                    )}
                                                </div>

                                                {/* Row 2: size, qty and price (read-only) */}
                                                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                                                    <div>
                                                        <label style={{ fontSize: '10px', color: '#64748b', fontWeight: 'bold', display: 'block', marginBottom: '3px' }}>SIZE</label>
                                                        {item._isNew && item.productId ? (
                                                            <select
                                                                className="ma-order-edit-input"
                                                                style={{ width: '100px', padding: '4px 8px' }}
                                                                value={item.size || ''}
                                                                onChange={(e) => handleItemChange(idx, 'size', e.target.value)}
                                                            >
                                                                <option value="">-- Size --</option>
                                                                {(productList.find(p => p._id === item.productId)?.sizes || []).map(s => (
                                                                    <option key={s.size} value={s.size}>{s.size} (Stock: {s.quantity})</option>
                                                                ))}
                                                            </select>
                                                        ) : (
                                                            <input
                                                                type="text"
                                                                className="ma-order-edit-input"
                                                                style={{ width: '80px', padding: '4px 8px' }}
                                                                value={item.size || ''}
                                                                onChange={(e) => handleItemChange(idx, 'size', e.target.value)}
                                                            />
                                                        )}
                                                    </div>
                                                    <div>
                                                        <label style={{ fontSize: '10px', color: '#64748b', fontWeight: 'bold', display: 'block', marginBottom: '3px' }}>QTY</label>
                                                        <input
                                                            type="number"
                                                            min="1"
                                                            className="ma-order-edit-input"
                                                            style={{ width: '80px', padding: '4px 8px' }}
                                                            value={item.quantity || 1}
                                                            onChange={(e) => handleItemChange(idx, 'quantity', e.target.value)}
                                                        />
                                                    </div>
                                                    {/* Price is READ-ONLY — set at product creation */}
                                                    <div>
                                                        <label style={{ fontSize: '10px', color: '#94a3b8', fontWeight: 'bold', display: 'block', marginBottom: '3px' }}>UNIT PRICE (Rs.) — fixed</label>
                                                        <input
                                                            type="text"
                                                            readOnly
                                                            className="ma-order-edit-input"
                                                            style={{ width: '100px', padding: '4px 8px', background: '#f1f5f9', cursor: 'not-allowed', color: '#475569' }}
                                                            value={item.price || 0}
                                                        />
                                                    </div>
                                                    <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
                                                        <div style={{ fontWeight: 700, color: '#111827', fontSize: '14px' }}>Rs. {Number((item.price || 0) * (item.quantity || 1)).toLocaleString()}</div>
                                                        <div style={{ fontSize: '10px', color: 'var(--ma-text-muted)' }}>subtotal</div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Customer Profiling & Destination Card */}
                        <div className="ma-order-user-info">
                            <div className="ma-order-content-card">
                                <div className="ma-order-card-header">👤 Customer Profiling</div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                    <div className="ma-order-data-field">
                                        <span className="ma-order-field-label">Full Name</span>
                                        {!isEditing ? (
                                            <span className="ma-order-field-value">{order.customer?.fullname || order.customerName || 'Unknown'}</span>
                                        ) : (
                                            <input 
                                                type="text" 
                                                className="ma-order-edit-input" 
                                                value={formData.customerName}
                                                onChange={(e) => handleFieldChange('customerName', e.target.value)}
                                                placeholder="Customer Full Name"
                                            />
                                        )}
                                    </div>
                                    <div className="ma-order-data-field">
                                        <span className="ma-order-field-label">Email</span>
                                        {!isEditing ? (
                                            <span className="ma-order-field-value">{order.customer?.email || 'N/A'}</span>
                                        ) : (
                                            <input 
                                                type="email" 
                                                className="ma-order-edit-input" 
                                                value={formData.email}
                                                onChange={(e) => handleFieldChange('email', e.target.value)}
                                                placeholder="customer@example.com"
                                            />
                                        )}
                                    </div>
                                    <div className="ma-order-data-field">
                                        <span className="ma-order-field-label">Phone Number</span>
                                        {!isEditing ? (
                                            <span className="ma-order-field-value">{order.customer?.phone || order.number || 'N/A'}</span>
                                        ) : (
                                            <input 
                                                type="text" 
                                                className="ma-order-edit-input" 
                                                value={formData.phone}
                                                onChange={(e) => handleFieldChange('phone', e.target.value)}
                                                placeholder="98XXXXXXXX"
                                            />
                                        )}
                                    </div>
                                    <div className="ma-order-data-field">
                                        <span className="ma-order-field-label">Optional Phone</span>
                                        {!isEditing ? (
                                            <span className="ma-order-field-value">{order.customer?.optionalNumber || order.optionalNumber || 'N/A'}</span>
                                        ) : (
                                            <input 
                                                type="text" 
                                                className="ma-order-edit-input" 
                                                value={formData.optionalNumber}
                                                onChange={(e) => handleFieldChange('optionalNumber', e.target.value)}
                                                placeholder="Secondary Phone (Optional)"
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="ma-order-content-card">
                                <div className="ma-order-card-header">📍 Final Destination</div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                    <div className="ma-order-data-field">
                                        <span className="ma-order-field-label">Address</span>
                                        {!isEditing ? (
                                            <span className="ma-order-field-value">{order.customer?.address || order.address || 'N/A'}</span>
                                        ) : (
                                            <input 
                                                type="text" 
                                                className="ma-order-edit-input" 
                                                value={formData.address}
                                                onChange={(e) => handleFieldChange('address', e.target.value)}
                                                placeholder="Street Address, Area"
                                            />
                                        )}
                                    </div>
                                    <div className="ma-order-data-field">
                                        <span className="ma-order-field-label">Landmark</span>
                                        {!isEditing ? (
                                            <span className="ma-order-field-value" style={{ fontStyle: 'italic', fontSize: '13px' }}>{order.customer?.landMark || order.landMark || 'N/A'}</span>
                                        ) : (
                                            <input 
                                                type="text" 
                                                className="ma-order-edit-input" 
                                                value={formData.landMark}
                                                onChange={(e) => handleFieldChange('landMark', e.target.value)}
                                                placeholder="Nearby Landmark"
                                            />
                                        )}
                                    </div>
                                    <div className="ma-order-data-field">
                                        <span className="ma-order-field-label">City</span>
                                        {!isEditing ? (
                                            <span className="ma-order-field-value">{order.customer?.city || 'N/A'}</span>
                                        ) : (
                                            <input 
                                                type="text" 
                                                className="ma-order-edit-input" 
                                                value={formData.city}
                                                onChange={(e) => handleFieldChange('city', e.target.value)}
                                                placeholder="City"
                                            />
                                        )}
                                    </div>
                                    <div className="ma-order-data-field">
                                        <span className="ma-order-field-label">Country</span>
                                        {!isEditing ? (
                                            <span className="ma-order-field-value">{order.customer?.country || 'Nepal'}</span>
                                        ) : (
                                            <input 
                                                type="text" 
                                                className="ma-order-edit-input" 
                                                value={formData.country}
                                                onChange={(e) => handleFieldChange('country', e.target.value)}
                                                placeholder="Country"
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Physical Specs (Height & Weight) Card */}
                        <div className="ma-order-content-card" style={{ marginTop: '20px' }}>
                            <div className="ma-order-card-header">📏 Physical Measurements (Manual Order Specs)</div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                                <div className="ma-order-data-field">
                                    <span className="ma-order-field-label">Height</span>
                                    {!isEditing ? (
                                        <span className="ma-order-field-value" style={{ fontWeight: 600 }}>{order.height || order.customer?.height || 'Not Specified'}</span>
                                    ) : (
                                        <input 
                                            type="text" 
                                            className="ma-order-edit-input" 
                                            value={formData.height}
                                            onChange={(e) => handleFieldChange('height', e.target.value)}
                                            placeholder="e.g. 5'7&quot; or 170cm"
                                        />
                                    )}
                                </div>
                                <div className="ma-order-data-field">
                                    <span className="ma-order-field-label">Weight</span>
                                    {!isEditing ? (
                                        <span className="ma-order-field-value" style={{ fontWeight: 600 }}>{order.weight || order.customer?.weight || 'Not Specified'}</span>
                                    ) : (
                                        <input 
                                            type="text" 
                                            className="ma-order-edit-input" 
                                            value={formData.weight}
                                            onChange={(e) => handleFieldChange('weight', e.target.value)}
                                            placeholder="e.g. 65kg"
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="ma-order-side-col">
                        {/* Financial Ledger */}
                        <div className="ma-order-action-card" style={{ marginBottom: '24px' }}>
                            <div className="ma-order-card-header">💰 Financial Ledger</div>
                            <div className="ma-order-pricing-list">
                                <div className="ma-order-price-row">
                                    <span>Base Revenue</span>
                                    <span>Rs. {Number(order.subTotal || 0).toLocaleString()}</span>
                                </div>
                                <div className="ma-order-price-row" style={{ alignItems: 'center' }}>
                                    <span>Logistics Fee</span>
                                    {!isEditing ? (
                                        <span>Rs. {order.serviceCharge || 0}</span>
                                    ) : (
                                        <input 
                                            type="number"
                                            min="0"
                                            className="ma-order-edit-input" 
                                            style={{ width: '90px', padding: '4px 8px', textAlign: 'right' }}
                                            value={formData.serviceCharge}
                                            onChange={(e) => handleFieldChange('serviceCharge', e.target.value)}
                                        />
                                    )}
                                </div>
                                <div className="ma-order-price-row" style={{ alignItems: 'center' }}>
                                    <span>Discount</span>
                                    {!isEditing ? (
                                        <span style={{ color: '#f87171' }}>- Rs. {order.discount || 0}</span>
                                    ) : (
                                        <input 
                                            type="number"
                                            min="0"
                                            className="ma-order-edit-input" 
                                            style={{ width: '90px', padding: '4px 8px', textAlign: 'right' }}
                                            value={formData.discount}
                                            onChange={(e) => handleFieldChange('discount', e.target.value)}
                                        />
                                    )}
                                </div>
                                <div className="ma-order-price-row grand-total" style={{ alignItems: 'center' }}>
                                    <span>Net Total</span>
                                    {!isEditing ? (
                                        <span style={{ color: 'var(--ma-primary)' }}>Rs. {Number(order.total || 0).toLocaleString()}</span>
                                    ) : (
                                        <input 
                                            type="number"
                                            min="0"
                                            className="ma-order-edit-input" 
                                            style={{ width: '120px', padding: '4px 8px', textAlign: 'right', fontWeight: 'bold' }}
                                            value={formData.total}
                                            onChange={(e) => handleFieldChange('total', e.target.value)}
                                        />
                                    )}
                                </div>
                                <div className="ma-order-price-row" style={{ alignItems: 'center' }}>
                                    <span style={{ fontWeight: 600, color: '#059669' }}>Advance Payment</span>
                                    {!isEditing ? (
                                        <span style={{ fontWeight: 600, color: '#059669' }}>Rs. {Number(order.advancePayment || 0).toLocaleString()}</span>
                                    ) : (
                                        <input 
                                            type="number"
                                            min="0"
                                            className="ma-order-edit-input" 
                                            style={{ width: '120px', padding: '4px 8px', textAlign: 'right', fontWeight: 'bold', color: '#059669' }}
                                            value={formData.advancePayment}
                                            onChange={(e) => handleFieldChange('advancePayment', e.target.value)}
                                        />
                                    )}
                                </div>
                                <div className="ma-order-price-row" style={{ alignItems: 'center', borderTop: '2px dashed var(--ma-border, #e5e7eb)', paddingTop: '10px', marginTop: '6px' }}>
                                    <span style={{ fontWeight: 700, fontSize: '15px' }}>Remaining Balance</span>
                                    {!isEditing ? (
                                        <span style={{ 
                                            fontWeight: 700, 
                                            fontSize: '15px', 
                                            color: ((order.remainingAmount !== undefined ? Number(order.remainingAmount) : Math.max(0, (order.total || 0) - (order.advancePayment || 0))) <= 0) ? '#059669' : '#dc2626' 
                                        }}>
                                            Rs. {Number(order.remainingAmount !== undefined ? order.remainingAmount : Math.max(0, (order.total || 0) - (order.advancePayment || 0))).toLocaleString()}
                                            {((order.remainingAmount !== undefined ? Number(order.remainingAmount) : Math.max(0, (order.total || 0) - (order.advancePayment || 0))) <= 0) && (
                                                <span style={{ fontSize: '11px', marginLeft: '6px', padding: '2px 6px', background: '#dcfce7', color: '#15803d', borderRadius: '4px' }}>PAID</span>
                                            )}
                                        </span>
                                    ) : (
                                        <span style={{ 
                                            fontWeight: 700, 
                                            fontSize: '15px', 
                                            color: Number(formData.remainingAmount || 0) <= 0 ? '#059669' : '#dc2626' 
                                        }}>
                                            Rs. {Number(formData.remainingAmount || 0).toLocaleString()}
                                            {Number(formData.remainingAmount || 0) <= 0 && (
                                                <span style={{ fontSize: '11px', marginLeft: '6px', padding: '2px 6px', background: '#dcfce7', color: '#15803d', borderRadius: '4px' }}>PAID</span>
                                            )}
                                        </span>
                                    )}
                                </div>
                            </div>
                            
                            {/* Payment Method & Status */}
                            <div style={{ marginTop: '20px', padding: '12px', background: 'rgba(0,0,0,0.03)', borderRadius: '6px' }}>
                                <div style={{ marginBottom: '8px' }}>
                                    <span className="ma-order-field-label">Payment Method</span>
                                    {!isEditing ? (
                                        <div style={{ fontSize: '13px', fontWeight: 600, textTransform: 'uppercase' }}>
                                            {order.paymentType || 'COD'}
                                        </div>
                                    ) : (
                                        <select 
                                            className="ma-order-edit-input"
                                            value={formData.paymentType}
                                            onChange={(e) => handleFieldChange('paymentType', e.target.value)}
                                        >
                                            <option value="cod">Cash on Delivery (COD)</option>
                                            <option value="Esewa">eSewa</option>
                                            <option value="Khalti">Khalti</option>
                                            <option value="Bank Transfer">Bank Transfer</option>
                                        </select>
                                    )}
                                </div>
                                <div>
                                    <span className="ma-order-field-label">Payment Status</span>
                                    {!isEditing ? (
                                        <div style={{ fontSize: '13px', fontWeight: 600, textTransform: 'capitalize' }}>
                                            {order.paymentStatus || 'pending'}
                                        </div>
                                    ) : (
                                        <select 
                                            className="ma-order-edit-input"
                                            value={formData.paymentStatus}
                                            onChange={(e) => handleFieldChange('paymentStatus', e.target.value)}
                                        >
                                            <option value="pending">Pending</option>
                                            <option value="paid">Paid</option>
                                            <option value="failed">Failed</option>
                                            <option value="refunded">Refunded</option>
                                        </select>
                                    )}
                                </div>
                            </div>

                            {isEditing && (
                                <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    <button 
                                        className="ma-order-btn-save" 
                                        style={{ width: '100%', padding: '12px' }}
                                        onClick={handleSaveFullOrder}
                                        disabled={loading}
                                    >
                                        {loading ? 'Saving...' : '💾 Save All Changes'}
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Quick Status Operations Card */}
                        <div className="ma-order-action-card">
                            <div className="ma-order-card-header">⚙️ Operations</div>
                            <p style={{ fontSize: '11px', color: 'var(--ma-text-muted)', marginBottom: '16px' }}>
                                Transition order status to trigger email notifications to the customer.
                            </p>
                            <OrdersForm 
                                detail={{
                                    orderStatus: {
                                        label: (order.orderStatus === 'pending' && 'Pending') || 
                                            (order.orderStatus === 'shipped' && 'Shipped') || 
                                            (order.orderStatus === 'canceled' && 'Canceled') || 
                                            (order.orderStatus === 'delevered' && 'Delivered') || 
                                            'Pending',
                                        value: order.orderStatus
                                    },
                                }} 
                                submitEvent={submitQuickStatus} 
                                loading={loading} 
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default AdminOrdersView